import cartApi from '@/api/services/cart'
import * as objectId from 'bson-objectid'
import Util from '@/util'

const defaultState = () => {
  return {
    items: [],
    sale: null,
    total: null,
    saleResponse: null,
    saleError: null,
    paymentInfo: null,
    paymentTxn: null,
    customerInfo: {
      marketUserId: null,
      contactInfo: {
        name: {
          firstName: null,
          lastName: null
        },
        phoneNumber: null
      }
    },
    customerLoyalty: null
  }
}

const state = defaultState()

const getters = {
  getCartCount: (state) => {
    if (!state.items) {
      return 0;
    }
    return state.items.length
  },

  getSale: (state) => {
    return state.sale
  },

  totalSale: (state) => {
    if (!state.sale) {
      return null;
    }
    return state.sale.billRoundedAmount
  },

  cartItem: (state) => (itemNumber) => {
    if (!state.items) {
      return null;
    }
    return state.items.find(item => item.itemNumber === itemNumber)
  },

  deliveryInfo: (state) => {
    if (!state.sale) {
      return null;
    }
    return state.sale.deliveryInfo
  },

  customerInfo: (state) => {
    return state.customerInfo
  }
}

const actions = {
  addItem({ commit, state, rootState }, params) {
    let item = this.getters['catalog/getItem'](params.itemId)
    let longName = item.info.longName
    let shortName = item.info.shortName

    if (params.variantItemId) {
      item = item.variants.find(v => v.mid === params.variantItemId)
      // shortName = item.variantValues.map(vv => vv.value).join(' ')
    }

    const channelDefId = this.getters['business/channelDefId']
    const channel = this.getters['catalog/getChannel'](channelDefId)

    let cartItem = {
      itemId: item.mid,
      longName: longName,
      shortName: shortName,
      skuCode: item.info.skuCode,
      barCode: item.info.barCode,
      variantValues: item.variantValues,
      measuringUnit: item.info.measuringUnit,
      quantity: 1,
      channelDefId: channelDefId,
      unitPrice: this.getters['catalog/itemPrice'](item),
      channelName: channel.name,
      isPriceIncludesTax: item.priceInfo.isPriceIncludesTax,
      itemTaxCode: item.info.itemTaxCode,
      taxTypeIds: item.priceInfo.taxTypeIds,
      itemNature: item.info.itemNature,
      categoryId: item.info.categoryId,
      subCategoryId: item.info.subCategoryId,
      brandId: item.info.brandId,
      chargeIds: item.info.chargeIds,
      denyDiscount: item.priceInfo.denyDiscount,
      uid: objectId()
    }

    if (!Util.isValidPrice(cartItem.unitPrice) || Util.isEmpty(cartItem.quantity)) {
      if (params.errCb) {
        params.errCb({
          error: 'Unable to add item to cart. Please contact outlet for further assistance.'
        })
      }
      return
    }

    commit('addItem', cartItem)
    commit('updateSale', rootState)
    if (params.cb) {
      params.cb(state.items.find(i => i.uid === cartItem.uid))
    }
  },

  updateItem({ commit, rootState }, item) {
    commit('updateItem', item)
    commit('updateSale', rootState)
  },

  removeItem({ commit, rootState }, itemNumber) {
    commit('removeItem', itemNumber)
    commit('updateSale', rootState)
  },

  updateSale({ commit, rootState }) {
    commit('updateSale', rootState)
  },

  updateCustomer({ commit, state }, customerInfo) {
    commit('updateCustomer', customerInfo)
  },

  updateDeliveryInfo({ commit, rootState }, deliveryInfo) {
    commit('updateDeliveryInfo', deliveryInfo)
    commit('updateSale', rootState)
  },

  updatePaymentInfo({ commit }, paymentInfo) {
    commit('updatePaymentInfo', paymentInfo)
  },

  updateSaleField({ commit, state, rootState }, saleField) {
    commit('updateSaleField', saleField)
  },

  saveSale({ commit, dispatch, state, rootState }, otherParams) {
    let params = {
      outletId: rootState.business.outletId,
      data: Util.removeFalsyValues({
        sale: state.sale,
        paymentInfo: state.paymentInfo,
        'g-recaptcha-response': otherParams['g-recaptcha-response']
      })
    };

    cartApi.saveSale(params).then(saleResponse => {
      // commit('resetState')
      if (saleResponse && saleResponse.sale) {
        commit('updateSaleResponse', saleResponse.sale)
        if (saleResponse.sale.paymentInfo) {
          commit('updatePaymentTxn', saleResponse.sale.paymentInfo)
        }
      }

      if (otherParams.cb) {
        otherParams.cb()
      }
    }).catch(error => {
      if (error.response.status === 401 || error.code === 'ERR_UNAUTHORIZED') {
        dispatch('user/signOut', { enforceSignin: true }, { root: true }).then(() => {
          if (otherParams.cb) {
            otherParams.cb(new Error('ERR_UNAUTHORIZED'));
          }
        });
      } else {
        commit('setSaleError', error);
        if (otherParams.cb) {
          otherParams.cb(error);
        }
      }
    });
  },

  getSale({ commit, state, rootState }, { callback }) {
    let params = {
      outletId: rootState.business.outletId,
      saleId: state.sale.mid
    }
    cartApi.getSale(params).then(saleResponse => {
      if (saleResponse) {
        commit('updateSaleResponse', saleResponse)
        if (saleResponse.paymentInfo) {
          commit('updatePaymentTxn', saleResponse.paymentInfo)
        }
      }

      if (callback) {
        return callback();
      }
    }).catch(error => {
      commit('setSaleError', error)

      if (callback) {
        return callback();
      }
    })
  },

  verifyAndPostPayment({ commit, state, rootState }, { payment, callback }) {
    const params = {
      outletId: rootState.business.outletId,
      saleId: state.sale.mid,
      data: payment
    };
    cartApi.verifyAndPostPayment(params).then(saleResponse => {
      if (saleResponse) {
        commit('updateSaleResponse', saleResponse)
        if (saleResponse.paymentInfo) {
          commit('updatePaymentTxn', saleResponse.paymentInfo)
        }
      }

      if (callback) {
        return callback();
      }
    }).catch(error => {
      commit('setSaleError', error)
      if (callback) {
        return callback();
      }
    })
  },

  fetchCustomerLoyaltyPoints({ commit, state, rootState, dispatch }) {
    const user = rootState.user.user;
    const outletId = rootState.business.outletId;
    const anyOutletId = !Util.isEmpty(rootState.business.listingVOs) ? rootState.business.listingVOs[0].listingId : null;

    if (!user || !(outletId || anyOutletId)) return;

    const params = {
      outletId: outletId || anyOutletId,
      data: {
        phoneNumber: user ? user.phoneNumber : null
      }
    };
    let updtdCustomerInfo

    cartApi.fetchCustomerLoyaltyPoints(params).then(response => {
      if (response) {
        commit('updateCustomerLoyalty', response);
        updtdCustomerInfo = Util.cloneObj(response);
        delete updtdCustomerInfo.loyaltyValue;
        commit('updateCustomer', updtdCustomerInfo);
      }
    }).catch(error => {
      if (error.response.status === 401 || error.code === 'ERR_UNAUTHORIZED') {
        dispatch('user/signOut', { enforceSignin: true }, { root: true }).then(() => {
          dispatch('fetchCustomerLoyaltyPoints')
        });
      }
    })
  },

  resetSaleErrors({ commit }) {
    commit('setSaleError', null)
  },

  resetState({ commit }) {
    commit('resetState')
  }
}

const mutations = {
  addItem(state, item) {
    state.items.push(item)
  },

  updateItem(state, item) {
    const index = state.items.findIndex((x) => x.uid === item.uid)

    if (index > -1) {
      state.items[index] = item
    }
  },

  removeItem(state, itemNumber) {
    var index = state.items.findIndex((x) => x.itemNumber === itemNumber)

    if (index > -1) {
      state.items.splice(index, 1)
    }
  },

  createSale(state, sale) {
    state.sale = sale
  },

  setSaleError(state, error) {
    state.saleError = error
  },

  updateCustomer(state, customerInfo) {
    let val = Object.assign(state.customerInfo, customerInfo)
    state.customerInfo = val
  },

  updateDeliveryInfo(state, deliveryInfo) {
    state.sale.deliveryInfo.id = deliveryInfo.id;
    state.sale.deliveryInfo.label = deliveryInfo.label;
    state.sale.deliveryInfo.mode = deliveryInfo.mode;
    state.sale.deliveryInfo.contactInfo = deliveryInfo.contactInfo;
    state.sale.deliveryInfo.address = deliveryInfo.address;
    state.sale.deliveryInfo.deliveryDate = deliveryInfo.deliveryDate;
  },

  updatePaymentInfo(state, paymentInfo) {
    state.paymentInfo = {
      paymentId: objectId(),
      pid: paymentInfo.pid,
      mode: paymentInfo.mid,
      amount: this.getters['cart/totalSale']
    }
  },

  updateSaleField(state, saleField) {
    state.sale[saleField.field] = saleField.value
  },

  updatePaymentTxn(state, paymentInfo) {
    let paymentTxn = null
    if (paymentInfo && paymentInfo.payments) {
      paymentTxn = paymentInfo.payments.find(payment => state.paymentInfo.paymentId.toString() === payment.paymentId)
    }
    state.paymentTxn = paymentTxn
  },

  updateSaleResponse(state, saleResponse) {
    state.saleResponse = saleResponse
  },

  updateCustomerLoyalty(state, customerLoyalty) {
    state.customerLoyalty = customerLoyalty
  },

  resetState(state) {
    Object.assign(state, defaultState())
  },

  updateSale(state, rootState) {
    const RL = window.RL
    const resourceId = rootState.business.resourceId
    const fetchArr = function (map) {
      if (!map) {
        return null
      }
      return Object.values(map)
    }
    const params = {
      discounts: resourceId ? [] : this.getters['catalog/getApplicableDeals'],
      charges: resourceId ? [] : fetchArr(rootState.catalog.charges),
      categories: fetchArr(rootState.catalog.categories),
      channelvos: fetchArr(rootState.catalog.channelvos),
      taxTypes: fetchArr(rootState.catalog.taxTypes),
      brands: fetchArr(rootState.catalog.brands),
      categoryGroups: fetchArr(rootState.catalog.categoryGroups),
      taxArea: rootState.catalog.taxArea,
      transactionEnd: this.getters['business/transactionEnd'],
      billRoundingMode: this.getters['business/billRoundingMode'],
      branchTime: Util.formatDate({ zoneId: this.getters['business/timeZone'].zoneId })
    }

    let sale = {}

    if (state.sale) {
      sale = state.sale
    } else {
      const channelDefId = this.getters['business/channelDefId']
      const channel = this.getters['catalog/getChannel'](channelDefId)
      const dMode = this.getters['business/deliveryModes'] ? this.getters['business/deliveryModes'][0] : null
      let deliveryInfo = {
        mode: dMode
      }
      let customerInfo = state.customerInfo
      let serviceLabel

      if (resourceId) {
        serviceLabel = this.getters['business/resource'](resourceId)
        deliveryInfo = {}
      }

      sale = {
        billSubTotal: 0,
        chargeTotal: 0,
        itemCount: 0,
        transactionType: 'Sale',
        // channel: 'MARKET',
        currency: this.getters['business/currency'],
        // saleStatus: 'Open',
        channelDefId: channelDefId,
        channelName: channel.name,
        groupId: channel.groupId,
        branchId: this.getters['business/branchId'],
        mid: objectId(),
        customerInfo: customerInfo,
        deliveryInfo: deliveryInfo,
        serviceLabel: serviceLabel,
        saleItems: state.items
      }
    }

    if (!RL) {
      state.sale = sale;
      return
    }
    try {
      state.sale = RL.saleutil.calculateForSale(sale, params)
    } catch (error) {
      console.log(error)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
